import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { companiesApi } from '@/api/companies.api';
import { storesApi } from '@/api/stores.api';
import { warehouseApi } from '@/api/warehouse.api';
import { hrApi } from '@/api/hr.api';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { setSelectedCompany } from '@/store/appSlice';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MasterLocaleSelects } from '@/components/master/MasterLocaleSelects';
import { Select } from '@/components/ui/Select';
import { HrWeekPatternsSection } from '@/modules/hr/components/HrWeekPatternsSection';
import type { CompanyRequest } from '@/types/company';
import type { HrWeekPattern } from '@/types/hr';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';

const steps = [
  'Company',
  'Store',
  'Warehouse',
  'Calendar',
  'HR',
  'Finance',
  'Go Live',
] as const;

export function EnterpriseSetupWizard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState(0);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [companyForm, setCompanyForm] = useState<CompanyRequest>({
    name: '',
    code: '',
    countryCode: 'IN',
    currencyCode: 'INR',
    timezone: 'Asia/Kolkata',
  });
  const [storeForm, setStoreForm] = useState({ name: '', code: '' });
  const [warehouseForm, setWarehouseForm] = useState({ name: '', code: '' });
  const [weekPatterns, setWeekPatterns] = useState<HrWeekPattern[]>([]);
  const [selectedPatternId, setSelectedPatternId] = useState<number | ''>('');
  const [deptForm, setDeptForm] = useState({ name: 'General', code: 'GEN' });
  const [setupStatus, setSetupStatus] = useState<Awaited<ReturnType<typeof companiesApi.setupStatus>> | null>(null);

  const refreshStatus = useCallback(async () => {
    try {
      setSetupStatus(await companiesApi.setupStatus());
    } catch {
      /* optional */
    }
  }, []);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus, step]);

  const createCompany = async () => {
    setSaving(true);
    try {
      const company = await companiesApi.create(companyForm);
      setCompanyId(company.id);
      dispatch(setSelectedCompany(company));
      notify.success('Company created');
      setStep(1);
    } catch (err) {
      showApiError(err, 'Failed to create company');
    } finally {
      setSaving(false);
    }
  };

  const createStore = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      await storesApi.create({ ...storeForm, companyId });
      notify.success('Store created');
      setStep(2);
    } catch (err) {
      showApiError(err, 'Failed to create store');
    } finally {
      setSaving(false);
    }
  };

  const createWarehouse = async () => {
    setSaving(true);
    try {
      await warehouseApi.createWarehouse({ name: warehouseForm.name, code: warehouseForm.code });
      notify.success('Warehouse created');
      setStep(3);
    } catch (err) {
      showApiError(err, 'Failed to create warehouse');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!companyId || step !== 3) return;
    void companiesApi.listWeekPatterns(companyId)
      .then((rows) => {
        setWeekPatterns(rows);
        setSelectedPatternId((current) => {
          if (current) return current;
          return rows.find((p) => p.active)?.id ?? (rows[0]?.id ?? '');
        });
      })
      .catch((e) => showApiError(e, 'Failed to load week patterns'));
  }, [companyId, step]);

  const saveCalendar = async () => {
    if (!companyId || !selectedPatternId) {
      notify.error('Create or select a week pattern');
      return;
    }
    setSaving(true);
    try {
      await companiesApi.updateWorkCalendar(companyId, { weekPatternId: Number(selectedPatternId) });
      notify.success('Work calendar configured');
      setStep(4);
    } catch (err) {
      showApiError(err, 'Failed to save calendar');
    } finally {
      setSaving(false);
    }
  };

  const createDepartment = async () => {
    setSaving(true);
    try {
      await hrApi.createDepartment({ name: deptForm.name, code: deptForm.code });
      notify.success('Department created');
      setStep(5);
    } catch (err) {
      showApiError(err, 'Failed to create department');
    } finally {
      setSaving(false);
    }
  };

  const finishSetup = () => {
    notify.success('Enterprise setup complete');
    navigate(MODULE_HUB_PATH);
  };

  return (
    <div className="mx-auto max-w-2xl py-8">
      <PageHeader
        title="Enterprise setup"
        description="Organization → Company → Store → Warehouse → Calendar → HR → Finance → Go Live"
      />

      <ol className="mb-8 flex flex-wrap gap-2 text-xs">
        {steps.map((label, i) => (
          <li
            key={label}
            className={`rounded-full px-3 py-1 ${
              i === step ? 'bg-primary-600 text-white' : i < step ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200'
            }`}
          >
            {i < step ? <CheckCircleIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> : null} {label}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="grid gap-3 rounded-xl border bg-white p-6 dark:bg-slate-900">
          <Input label="Company name" value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} />
          <Input label="Code" value={companyForm.code} onChange={(e) => setCompanyForm({ ...companyForm, code: e.target.value })} />
          <MasterLocaleSelects
            value={{
              countryCode: companyForm.countryCode ?? '',
              currencyCode: companyForm.currencyCode ?? '',
              timezone: companyForm.timezone ?? '',
            }}
            onChange={(patch) => setCompanyForm({ ...companyForm, ...patch })}
          />
          <Button onClick={createCompany} disabled={saving || !companyForm.name || !companyForm.code}>
            Create company
          </Button>
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-3 rounded-xl border bg-white p-6 dark:bg-slate-900">
          <Input label="Store name" value={storeForm.name} onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })} />
          <Input label="Store code" value={storeForm.code} onChange={(e) => setStoreForm({ ...storeForm, code: e.target.value })} />
          <Button onClick={createStore} disabled={saving || !storeForm.name || !storeForm.code}>
            Create store
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="grid gap-3 rounded-xl border bg-white p-6 dark:bg-slate-900">
          <Input label="Warehouse name" value={warehouseForm.name} onChange={(e) => setWarehouseForm({ ...warehouseForm, name: e.target.value })} />
          <Input label="Warehouse code" value={warehouseForm.code} onChange={(e) => setWarehouseForm({ ...warehouseForm, code: e.target.value })} />
          <Button onClick={createWarehouse} disabled={saving || !warehouseForm.name || !warehouseForm.code}>
            Create warehouse
          </Button>
        </div>
      )}

      {step === 3 && companyId && (
        <div className="grid gap-4 rounded-xl border bg-white p-6 dark:bg-slate-900">
          <Select
            label="Assign week pattern"
            value={selectedPatternId === '' ? '' : String(selectedPatternId)}
            options={[
              { value: '', label: weekPatterns.length ? 'Select…' : 'Create a pattern below' },
              ...weekPatterns.filter((p) => p.active).map((p) => ({
                value: String(p.id),
                label: `${p.name} (${p.code})`,
              })),
            ]}
            onChange={(v) => setSelectedPatternId(v ? Number(v) : '')}
          />
          <HrWeekPatternsSection
            companyId={companyId}
            compact
            selectedId={selectedPatternId === '' ? null : Number(selectedPatternId)}
            onPatternsChange={setWeekPatterns}
            onSelect={(p) => setSelectedPatternId(p.id)}
          />
          <Button onClick={saveCalendar} disabled={saving || !selectedPatternId}>
            Save calendar
          </Button>
        </div>
      )}

      {step === 4 && (
        <div className="grid gap-3 rounded-xl border bg-white p-6 dark:bg-slate-900">
          <Input label="Department name" value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} />
          <Input label="Department code" value={deptForm.code} onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value })} />
          <Button onClick={createDepartment} disabled={saving}>
            Create department
          </Button>
          <Button variant="secondary" onClick={() => setStep(5)}>
            Skip for now
          </Button>
        </div>
      )}

      {step === 5 && (
        <div className="rounded-xl border bg-white p-6 dark:bg-slate-900">
          <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">
            Configure chart of accounts, fiscal year, and tax rules in the Finance module for your active company.
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate('/finance')}>
              Open Finance
            </Button>
            <Button onClick={() => setStep(6)}>Continue</Button>
          </div>
        </div>
      )}

      {step === 6 && (
        <div className="rounded-xl border bg-white p-6 dark:bg-slate-900">
          {setupStatus?.readyForGoLive ? (
            <p className="mb-4 text-emerald-700">All required setup steps are complete.</p>
          ) : (
            <ul className="mb-4 list-disc pl-5 text-sm text-amber-700">
              {(setupStatus?.pendingSteps ?? ['Complete remaining steps']).map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          )}
          <Button onClick={finishSetup}>Go Live</Button>
        </div>
      )}
    </div>
  );
}
